<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDateTime, toApiDateTime } from '@yuksalish/ui'
import { usePlanningApi } from '../composables/usePlanningApi'
import { SHIFT_VALUES } from '../types/planning'
import type { Machine, MachineAssignment, ProductionOrder } from '../types/planning'

/**
 * Machine loading: which machine carries which order/stage/lot, on which shift.
 *
 * Machine state comes from `/production-execution/machines`, the bookings from
 * `/production-execution/assignments?type=machine`, and the order context from
 * `/production-orders`. Booking and releasing a machine writes real assignments.
 */

const { t } = useI18n()
const auth = useAuthStore()
const {
  listMachines,
  listAllAssignments,
  listAllProductionOrders,
  listOrderStages,
  listOrderLots,
  createAssignment,
  endAssignment,
} = usePlanningApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('production_orders.manage'))

const machines = ref<Machine[]>([])
const assignments = ref<MachineAssignment[]>([])
const orders = ref<ProductionOrder[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busyId = ref<number | null>(null)
const statusFilter = ref('')
const showForm = ref(false)

const form = ref({
  machine_id: '' as number | '',
  order_id: '' as number | '',
  stage_code: '',
  lot_number: '',
  shift: SHIFT_VALUES[0] as string,
  notes: '',
})

const formStages = ref<Array<{ stage_code: string; stage_name: string }>>([])
const formLots = ref<Array<{ lot_number: string; current_stage_code: string }>>([])
const formLoading = ref(false)

const statuses = ['running', 'idle', 'down', 'setup']

const statusCounts = computed(() => ({
  running: machines.value.filter((machine) => machine.status === 'running').length,
  idle: machines.value.filter((machine) => machine.status === 'idle').length,
  down: machines.value.filter((machine) => machine.status === 'down').length,
  setup: machines.value.filter((machine) => machine.status === 'setup').length,
}))

const activeMachineAssignments = computed(() =>
  assignments.value.filter((entry) => entry.status === 'active' && entry.machine_id !== null),
)

const bookingByMachine = computed(() => {
  const map = new Map<string, MachineAssignment>()
  for (const entry of activeMachineAssignments.value) {
    if (entry.machine_id !== null) map.set(String(entry.machine_id), entry)
  }
  return map
})

const orderById = computed(() => {
  const map = new Map<string, ProductionOrder>()
  for (const order of orders.value) map.set(String(order.id), order)
  return map
})

const visibleMachines = computed(() => {
  const term = statusFilter.value
  const list = term ? machines.value.filter((machine) => machine.status === term) : machines.value
  return [...list].sort((a, b) => a.name.localeCompare(b.name))
})

/** A machine whose booking disagrees with the order the machine reports running. */
function isMismatched(machine: Machine): boolean {
  const booking = bookingByMachine.value.get(String(machine.id))
  if (!booking) return false
  if (machine.current_order_id === null) return false
  return String(booking.order_id) !== String(machine.current_order_id)
}

const mismatches = computed(() => machines.value.filter((machine) => isMismatched(machine)))

const unbookedRunning = computed(() =>
  machines.value.filter(
    (machine) => machine.status === 'running' && !bookingByMachine.value.has(String(machine.id)),
  ),
)

function orderLabel(order: ProductionOrder): string {
  return `${order.mo_code} — ${order.product_name || ''}`.trim()
}

async function load() {
  loading.value = true
  error.value = ''
  actionError.value = ''
  try {
    const [machineList, assignmentList, orderList] = await Promise.all([
      listMachines(),
      listAllAssignments({ type: 'machine' }),
      listAllProductionOrders({}),
    ])
    machines.value = machineList
    assignments.value = assignmentList
    orders.value = orderList
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

/** Lot/stage options come from the selected order, so the booking is specific. */
async function loadFormContext() {
  formStages.value = []
  formLots.value = []
  form.value.stage_code = ''
  form.value.lot_number = ''
  if (!form.value.order_id) return
  formLoading.value = true
  try {
    const [stages, lots] = await Promise.all([
      listOrderStages(Number(form.value.order_id)),
      listOrderLots(Number(form.value.order_id)),
    ])
    formStages.value = stages.map((stage) => ({
      stage_code: stage.stage_code,
      stage_name: stage.stage_name,
    }))
    formLots.value = lots
      .filter((lot) => lot.status === 'active')
      .map((lot) => ({ lot_number: lot.lot_number, current_stage_code: lot.current_stage_code }))
    if (formStages.value.length) form.value.stage_code = formStages.value[0].stage_code
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    formLoading.value = false
  }
}

function openForm(machine?: Machine) {
  actionError.value = ''
  actionMessage.value = ''
  form.value = {
    machine_id: machine ? machine.id : '',
    order_id: '',
    stage_code: '',
    lot_number: '',
    shift: SHIFT_VALUES[0],
    notes: '',
  }
  formStages.value = []
  formLots.value = []
  showForm.value = true
}

async function submit() {
  actionError.value = ''
  actionMessage.value = ''
  if (!form.value.machine_id) {
    actionError.value = t('planning.machineLoading.invalidMachine')
    return
  }
  if (!form.value.order_id) {
    actionError.value = t('planning.machineLoading.invalidOrder')
    return
  }
  if (!form.value.stage_code) {
    actionError.value = t('planning.machineLoading.invalidStage')
    return
  }
  busyId.value = Number(form.value.machine_id)
  try {
    await createAssignment({
      machine_id: Number(form.value.machine_id),
      order_id: Number(form.value.order_id),
      stage_code: form.value.stage_code,
      lot_number: form.value.lot_number || null,
      shift: form.value.shift,
      assigned_at: toApiDateTime(new Date()),
      notes: form.value.notes.trim() || undefined,
    })
    actionMessage.value = t('planning.machineLoading.booked')
    showForm.value = false
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = null
  }
}

async function release(machine: Machine) {
  const booking = bookingByMachine.value.get(String(machine.id))
  if (!booking) return
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('planning.machineLoading.releaseConfirm', { machine: machine.name }))) return
  busyId.value = machine.id
  try {
    await endAssignment(booking.id)
    actionMessage.value = t('planning.machineLoading.released')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = null
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('planning.machineLoading.eyebrow') }}</p>
      <h2>{{ t('planning.machineLoading.title') }}</h2>
      <p>{{ t('planning.machineLoading.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <select v-model="statusFilter" class="filter-select">
      <option value="">{{ t('planning.machineLoading.allStatuses') }}</option>
      <option v-for="status in statuses" :key="status" :value="status">
        {{ t(`planning.machineStatus.${status}`) }}
      </option>
    </select>
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('planning.common.refresh') }}
    </button>
    <span class="spacer" />
    <button v-if="canManage" class="btn-primary" @click="openForm()">
      {{ t('planning.machineLoading.newBooking') }}
    </button>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('planning.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('planning.common.retry') }}</button>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-label">{{ t('planning.machineStatus.running') }}</span>
        <span class="summary-value">{{ statusCounts.running }}</span>
        <span class="summary-label">{{ t('planning.machineLoading.ofTotal', { total: machines.length }) }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('planning.machineStatus.idle') }}</span>
        <span class="summary-value">{{ statusCounts.idle }}</span>
        <span class="summary-label">{{ t('planning.machineLoading.booked', { count: activeMachineAssignments.length }) }}</span>
      </div>
      <div class="summary-card" :class="{ attention: statusCounts.down + statusCounts.setup > 0 }">
        <span class="summary-label">{{ t('planning.machineLoading.unavailable') }}</span>
        <span class="summary-value">{{ statusCounts.down + statusCounts.setup }}</span>
        <span class="summary-label">
          {{ t('planning.machineStatus.down') }}: {{ statusCounts.down }} ·
          {{ t('planning.machineStatus.setup') }}: {{ statusCounts.setup }}
        </span>
      </div>
      <div class="summary-card" :class="{ attention: mismatches.length > 0 }">
        <span class="summary-label">{{ t('planning.machineLoading.mismatches') }}</span>
        <span class="summary-value">{{ mismatches.length }}</span>
        <span class="summary-label">{{ t('planning.machineLoading.mismatchHint') }}</span>
      </div>
    </div>

    <section v-if="mismatches.length || unbookedRunning.length" class="panel" style="margin-bottom: 18px">
      <div class="panel-header">
        <h3>{{ t('planning.machineLoading.attention') }}</h3>
      </div>
      <ul class="plain-list">
        <li v-for="machine in mismatches" :key="`mismatch-${machine.id}`">
          <strong>{{ machine.name }}</strong>
          <span>{{ t('planning.machineLoading.mismatchText', {
            booking: bookingByMachine.get(String(machine.id))?.order_code || '—',
            running: machine.current_order_code || '—',
          }) }}</span>
        </li>
        <li v-for="machine in unbookedRunning" :key="`unbooked-${machine.id}`">
          <strong>{{ machine.name }}</strong>
          <span>{{ t('planning.machineLoading.unbookedText', { order: machine.current_order_code || '—' }) }}</span>
        </li>
      </ul>
    </section>

    <section class="panel">
      <div class="panel-header">
        <h3>{{ t('planning.machineLoading.machines') }}</h3>
      </div>

      <div v-if="visibleMachines.length === 0" class="empty-inline">
        {{ t('planning.common.empty') }}
      </div>

      <div v-else class="table-scroll">
        <table class="data-table compact">
          <thead>
            <tr>
              <th>{{ t('planning.common.machine') }}</th>
              <th>{{ t('planning.common.status') }}</th>
              <th>{{ t('planning.machineLoading.currentOrder') }}</th>
              <th>{{ t('planning.machineLoading.bookedOrder') }}</th>
              <th>{{ t('planning.common.stage') }}</th>
              <th>{{ t('planning.common.lot') }}</th>
              <th>{{ t('planning.common.shift') }}</th>
              <th class="cell-num">{{ t('planning.machineLoading.progress') }}</th>
              <th>{{ t('planning.common.assignedAt') }}</th>
              <th class="cell-actions">{{ t('planning.common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="machine in visibleMachines" :key="machine.id">
              <td class="cell-name">
                {{ machine.name }}
                <span v-if="machine.fault_active" class="cell-sub status-critical">
                  {{ t('planning.machineLoading.fault') }}
                </span>
              </td>
              <td>
                <span
                  class="status-badge"
                  :class="machine.status === 'running' ? 'status-ok' : machine.status === 'idle' ? 'status-neutral' : 'status-critical'"
                >
                  {{ t(`planning.machineStatus.${machine.status}`) }}
                </span>
              </td>
              <td>{{ machine.current_order_code || '—' }}</td>
              <td>{{ bookingByMachine.get(String(machine.id))?.order_code || '—' }}</td>
              <td>
                {{ bookingByMachine.get(String(machine.id))?.stage_name
                  || bookingByMachine.get(String(machine.id))?.stage_code
                  || '—' }}
              </td>
              <td>{{ machine.current_lot_number || bookingByMachine.get(String(machine.id))?.lot_number || '—' }}</td>
              <td>
                {{ bookingByMachine.get(String(machine.id))?.shift
                  ? t(`planning.shift.${bookingByMachine.get(String(machine.id))?.shift}`)
                  : '—' }}
              </td>
              <td class="cell-num">
                <template v-if="orderById.get(String(machine.current_order_id))">
                  {{ orderById.get(String(machine.current_order_id))?.produced_quantity.toLocaleString() }}
                  /
                  {{ orderById.get(String(machine.current_order_id))?.planned_quantity.toLocaleString() }}
                </template>
                <template v-else>—</template>
              </td>
              <td class="cell-date">
                {{ formatDisplayDateTime(bookingByMachine.get(String(machine.id))?.assigned_at) || '—' }}
              </td>
              <td class="cell-actions">
                <template v-if="canManage">
                  <button class="link-action" :disabled="busyId === machine.id" @click="openForm(machine)">
                    {{ t('planning.machineLoading.book') }}
                  </button>
                  <button
                    v-if="bookingByMachine.get(String(machine.id))"
                    class="link-action danger"
                    :disabled="busyId === machine.id"
                    @click="release(machine)"
                  >
                    {{ t('planning.machineLoading.release') }}
                  </button>
                </template>
                <span v-else class="empty-inline">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </template>

  <div v-if="showForm" class="modal-overlay" @click.self="showForm = false">
    <form class="modal" @submit.prevent="submit">
      <h3>{{ t('planning.machineLoading.newBooking') }}</h3>

      <div class="field">
        <label>{{ t('planning.common.machine') }}</label>
        <select v-model="form.machine_id">
          <option value="">{{ t('planning.common.selectMachine') }}</option>
          <option v-for="machine in machines" :key="machine.id" :value="machine.id">
            {{ machine.name }} — {{ t(`planning.machineStatus.${machine.status}`) }}
          </option>
        </select>
      </div>

      <div class="field">
        <label>{{ t('planning.common.order') }}</label>
        <select v-model="form.order_id" @change="loadFormContext">
          <option value="">{{ t('planning.common.selectOrder') }}</option>
          <option v-for="order in orders" :key="order.id" :value="order.id">
            {{ orderLabel(order) }}
          </option>
        </select>
      </div>

      <div class="field">
        <label>{{ t('planning.common.stage') }}</label>
        <select v-model="form.stage_code" :disabled="formLoading || formStages.length === 0">
          <option value="">{{ t('planning.common.selectStage') }}</option>
          <option v-for="stage in formStages" :key="stage.stage_code" :value="stage.stage_code">
            {{ stage.stage_name }}
          </option>
        </select>
      </div>

      <div class="field">
        <label>{{ t('planning.common.lot') }} <small>{{ t('planning.common.optional') }}</small></label>
        <select v-model="form.lot_number" :disabled="formLots.length === 0">
          <option value="">{{ t('planning.common.noLot') }}</option>
          <option v-for="lot in formLots" :key="lot.lot_number" :value="lot.lot_number">
            {{ lot.lot_number }} — {{ lot.current_stage_code }}
          </option>
        </select>
      </div>

      <div class="field">
        <label>{{ t('planning.common.shift') }}</label>
        <select v-model="form.shift">
          <option v-for="value in SHIFT_VALUES" :key="value" :value="value">
            {{ t(`planning.shift.${value}`) }}
          </option>
        </select>
      </div>

      <div class="field">
        <label>{{ t('planning.common.notes') }} <small>{{ t('planning.common.optional') }}</small></label>
        <input v-model.trim="form.notes" type="text">
      </div>

      <p v-if="actionError" class="error-inline">{{ actionError }}</p>

      <div class="modal-actions">
        <button class="btn-secondary" type="button" @click="showForm = false">
          {{ t('planning.common.cancel') }}
        </button>
        <button class="btn-primary" type="submit" :disabled="busyId !== null">
          {{ t('planning.machineLoading.book') }}
        </button>
      </div>
    </form>
  </div>
</template>
